import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import StudioCard from "@/components/StudioCard";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, Radio, ShieldAlert, Instagram, Mail } from "lucide-react";

export default function HomeTab({ onOpen }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [engineer, setEngineer] = useState(false);
  const [sort, setSort] = useState("relevance");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (q) params.q = q;
      if (type !== "all") params.type = type;
      if (engineer) params.engineer = true;
      const { data } = await api.get("/studios", { params });
      setStudios(data.studios);
    } finally {
      setLoading(false);
    }
  }, [q, type, engineer, sort]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <div className="ss-rise">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">{t("find_studio")}</h1>
      <p className="text-muted-foreground text-sm mb-5">{t("app_tagline")}</p>

      {user && user.role !== "owner" && user.kyc_status !== "verified" && (
        <div data-testid="kyc-banner" className="flex items-center gap-2 mb-4 p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm">
          <ShieldAlert className="w-4 h-4 shrink-0" /> {t("kyc_banner")}
        </div>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="studio-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("search_placeholder")}
          className="pl-11 h-12 rounded-xl bg-card border-border"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          { id: "all", label: t("all_types") },
          { id: "recording", label: t("recording") },
          { id: "rehearsal", label: t("rehearsal") },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`filter-type-${f.id}`}
            onClick={() => setType(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              type === f.id ? "border-primary bg-primary/15 text-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          data-testid="filter-engineer"
          onClick={() => setEngineer((v) => !v)}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all flex items-center gap-1.5 ${
            engineer ? "border-emerald-500 bg-emerald-500/15 text-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"
          }`}
        >
          <Radio className="w-3.5 h-3.5" /> {t("with_engineer")}
        </button>

        <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
          <SlidersHorizontal className="w-4 h-4" />
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-card border border-border rounded-lg px-2 py-2 text-foreground text-sm"
          >
            <option value="relevance">{t("relevance")}</option>
            <option value="price_asc">{t("price_low")}</option>
            <option value="price_desc">{t("price_high")}</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => <div key={i} className="h-72 rounded-2xl bg-card/60 animate-pulse" />)}
        </div>
      ) : studios.length === 0 ? (
        <div data-testid="empty-studios" className="text-center py-20 border border-dashed border-border rounded-2xl">
          <p className="font-display text-xl font-bold">{t("no_studios")}</p>
          <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">{t("no_studios_desc")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {studios.map((s) => <StudioCard key={s.id} studio={s} onOpen={onOpen} />)}
        </div>
      )}

      <footer className="mt-16 pt-6 border-t border-border" data-testid="home-footer">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
              <span className="font-display font-extrabold text-primary text-xs">S</span>
            </div>
            <span>© {new Date().getFullYear()} StudioSync</span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="mailto:support@studiosync.store"
              data-testid="footer-email"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Mail className="w-4 h-4" /> support@studiosync.store
            </a>
            <a
              href="https://www.instagram.com/soundsync.store/"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram"
              className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
